import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RecipientService from '../../../services/RecipientService';
import BankService from '../../../services/BankService';
import Spinner from 'react-bootstrap/Spinner';
import AuthService from '../../../services/AuthService';
import Flags from "react-world-flags";

const RecipientDetailModal = ({ show, handleClose, recipient, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        firstName: '',
        lastName: '',
        city: '',
        country: '',
        email: '',
        enabled: false,
        lastModifiedBy: '',
        recipientAccountInfo: {
            id: null,
            recipientId: null,
            bankAccountInfos: [],
            walletAccountInfos: []
        }
    });
    const [banks, setBanks] = useState([]);
    const [touchedFields, setTouchedFields] = useState({
        bankAccountInfos: [],
        walletAccountInfos: []
    });
    const [errorMessages, setErrorMessages] = useState({
        bankAccountInfos: [],
        walletAccountInfos: []
    });
    const countries = [
        { name: "Cameroun", code: "CM" },
        { name: "Bénin", code: "BJ" },
        { name: "Burkina Faso", code: "BF" },
        { name: "Côte d'Ivoire", code: "CI" },
        { name: "Sénégal", code: "SN" },
    ];
    const countryToCodes = {
        "Cameroun": "237",
        "Bénin": "229",
        "Burkina Faso": "226",
        "Côte d'Ivoire": "225",
        "Sénégal": "221",
    };
    const walletTypes = [
        { value: "ORANGE_MONEY", label: "Orange Money" },
        { value: "MTN_MOBILE_MONEY", label: "MTN Mobile Money" },
        { value: "WAVE", label: "Wave" },
    ];

    useEffect(() => {
        if (recipient) {
            const { createdAt, modifiedAt, createdBy, ...updateableFields } = recipient;
            setFormData(updateableFields);
            const bankTouchedFields = updateableFields.recipientAccountInfo?.bankAccountInfos?.map(() => ({})) || [];
            const walletTouchedFields = updateableFields.recipientAccountInfo?.walletAccountInfos?.map(() => ({})) || [];
            const bankErrorMessages = updateableFields.recipientAccountInfo?.bankAccountInfos?.map(() => ({})) || [];
            const walletErrorMessages = updateableFields.recipientAccountInfo?.walletAccountInfos?.map(() => ({})) || [];
            setTouchedFields({
                bankAccountInfos: bankTouchedFields,
                walletAccountInfos: walletTouchedFields
            });
            setErrorMessages({
                bankAccountInfos: bankErrorMessages,
                walletAccountInfos: walletErrorMessages
            });
        }
    }, [recipient]);

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const banksData = await BankService.getAllBanks();
                const cameroonBanks = banksData.filter(bank => bank.country === "Cameroun");
                setBanks(cameroonBanks);
            } catch (error) {
                console.error('Error fetching banks:', error.message || error);
            }
        };
        fetchBanks();
    }, []);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            recipientAccountInfo: {
                ...prev.recipientAccountInfo,
                bankAccountInfos: prev.recipientAccountInfo.bankAccountInfos.map(info => ({
                    ...info,
                    city: prev.city,
                    country: prev.country
                })),
                walletAccountInfos: prev.recipientAccountInfo.walletAccountInfos.map(info => ({
                    ...info,
                    city: prev.city,
                    country: prev.country
                }))
            }
        }));
    }, [formData.city, formData.country]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const validateField = (field, value) => {
        switch (field) {
            case 'accountName':
            case 'branchName':
            case 'address':
                return value.trim() === '' ? 'Ce champ est obligatoire.' : '';
            case 'branchNumber':
                return /^\d{5}$/.test(value) ? '' : 'Le code agence doit contenir exactement 5 chiffres.';
            case 'accountNumber':
                return /^\d{11}$/.test(value) ? '' : 'Le numéro de compte doit contenir exactement 11 chiffres.';
            case 'accountKey':
                return /^\d{2}$/.test(value) ? '' : 'La clé de compte doit contenir exactement 2 chiffres.';
            case 'walletType':
                return value === '' ? 'Ce champ est obligatoire.' : '';
            case 'phoneNumber':
                return value.trim() === '' ? 'Ce champ est obligatoire.' : '';
            default:
                return '';
        }
    };

    const handleBankAccountInfoChange = (index, field, value) => {
        const updatedBankAccountInfos = [...formData.recipientAccountInfo.bankAccountInfos];
        updatedBankAccountInfos[index] = {
            ...updatedBankAccountInfos[index],
            [field]: value
        };
        setFormData({
            ...formData,
            recipientAccountInfo: {
                ...formData.recipientAccountInfo,
                bankAccountInfos: updatedBankAccountInfos
            }
        });
        const updatedTouchedFields = [...touchedFields.bankAccountInfos];
        updatedTouchedFields[index] = {
            ...updatedTouchedFields[index],
            [field]: true
        };
        setTouchedFields({
            ...touchedFields,
            bankAccountInfos: updatedTouchedFields
        });
        const updatedErrorMessages = [...errorMessages.bankAccountInfos];
        updatedErrorMessages[index] = {
            ...updatedErrorMessages[index],
            [field]: validateField(field, value)
        };
        setErrorMessages({
            ...errorMessages,
            bankAccountInfos: updatedErrorMessages
        });
    };

    const handleWalletAccountInfoChange = (index, field, value) => {
        const updatedWalletAccountInfos = [...formData.recipientAccountInfo.walletAccountInfos];
        updatedWalletAccountInfos[index] = {
            ...updatedWalletAccountInfos[index],
            [field]: value
        };
        setFormData({
            ...formData,
            recipientAccountInfo: {
                ...formData.recipientAccountInfo,
                walletAccountInfos: updatedWalletAccountInfos
            }
        });
        const updatedTouchedFields = [...touchedFields.walletAccountInfos];
        updatedTouchedFields[index] = {
            ...updatedTouchedFields[index],
            [field]: true
        };
        setTouchedFields({
            ...touchedFields,
            walletAccountInfos: updatedTouchedFields
        });
        const updatedErrorMessages = [...errorMessages.walletAccountInfos];
        updatedErrorMessages[index] = {
            ...updatedErrorMessages[index],
            [field]: validateField(field, value)
        };
        setErrorMessages({
            ...errorMessages,
            walletAccountInfos: updatedErrorMessages
        });
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    /*const handleSave = async () => {
        // Marquer tous les champs comme touchés
        const updatedBankTouchedFields = formData.recipientAccountInfo.bankAccountInfos.map(info => ({
            accountName: true,
            branchName: true,
            branchNumber: true,
            accountNumber: true,
            accountKey: true,
            address: true
        }));
        const updatedWalletTouchedFields = formData.recipientAccountInfo.walletAccountInfos.map(info => ({
            walletType: true,
            phoneNumber: true,
            accountName: true,
            address: true
        }));
        setTouchedFields({
            bankAccountInfos: updatedBankTouchedFields,
            walletAccountInfos: updatedWalletTouchedFields
        });

        // Valider tous les champs
        const bankErrors = formData.recipientAccountInfo.bankAccountInfos.map(info => ({
            accountName: validateField('accountName', info.accountName),
            branchName: validateField('branchName', info.branchName),
            branchNumber: validateField('branchNumber', info.branchNumber),
            accountNumber: validateField('accountNumber', info.accountNumber),
            accountKey: validateField('accountKey', info.accountKey),
            address: validateField('address', info.address)
        }));
        const walletErrors = formData.recipientAccountInfo.walletAccountInfos.map(info => ({
            walletType: validateField('walletType', info.walletType),
            phoneNumber: validateField('phoneNumber', info.phoneNumber),
            accountName: validateField('accountName', info.accountName),
            address: validateField('address', info.address)
        }));
        setErrorMessages({
            bankAccountInfos: bankErrors,
            walletAccountInfos: walletErrors
        });

        // Vérifier s'il y a des erreurs
        const hasBankErrors = bankErrors.some(err =>
            err.accountName || err.branchName || err.branchNumber || err.accountNumber || err.accountKey || err.address
        );
        const hasWalletErrors = walletErrors.some(err =>
            err.walletType || err.phoneNumber || err.accountName || err.address
        );

        if (hasBankErrors || hasWalletErrors) {
            toast.error('Veuillez remplir correctement tous les champs requis.');
            return;
        }

        setIsLoading(true);
        try {
            const updateData = {
                ...formData,
                lastModifiedBy: 'stephanezone',
                recipientAccountInfo: {
                    ...formData.recipientAccountInfo,
                    bankAccountInfos: formData.recipientAccountInfo.bankAccountInfos,
                    walletAccountInfos: formData.recipientAccountInfo.walletAccountInfos
                }
            };
            await RecipientService.updateRecipient(updateData);
            toast.success('Destinataire mis à jour avec succès!');
            onUpdate();
            handleClose();
            setIsEditing(false);
        } catch (error) {
            console.error('Error in updateRecipient:', error);
            toast.error('Erreur lors de la mise à jour du destinataire.');
        } finally {
            setIsLoading(false);
        }
    };*/

    const handleSave = async () => {
        // Récupérer l'utilisateur connecté
        const currentUser = AuthService.getCurrentUser();
        if (!currentUser) {
            toast.error('Utilisateur non connecté.');
            return;
        }

        // Marquer tous les champs comme touchés
        const updatedBankTouchedFields = formData.recipientAccountInfo.bankAccountInfos.map(info => ({
            accountName: true,
            branchName: true,
            branchNumber: true,
            accountNumber: true,
            accountKey: true,
            address: true
        }));
        const updatedWalletTouchedFields = formData.recipientAccountInfo.walletAccountInfos.map(info => ({
            walletType: true,
            phoneNumber: true,
            accountName: true,
            address: true
        }));
        setTouchedFields({
            bankAccountInfos: updatedBankTouchedFields,
            walletAccountInfos: updatedWalletTouchedFields
        });

        // Valider tous les champs
        const bankErrors = formData.recipientAccountInfo.bankAccountInfos.map(info => ({
            accountName: validateField('accountName', info.accountName),
            branchName: validateField('branchName', info.branchName),
            branchNumber: validateField('branchNumber', info.branchNumber),
            accountNumber: validateField('accountNumber', info.accountNumber),
            accountKey: validateField('accountKey', info.accountKey),
            address: validateField('address', info.address)
        }));
        const walletErrors = formData.recipientAccountInfo.walletAccountInfos.map(info => ({
            walletType: validateField('walletType', info.walletType),
            phoneNumber: validateField('phoneNumber', info.phoneNumber),
            accountName: validateField('accountName', info.accountName),
            address: validateField('address', info.address)
        }));
        setErrorMessages({
            bankAccountInfos: bankErrors,
            walletAccountInfos: walletErrors
        });

        // Vérifier s'il y a des erreurs
        const hasBankErrors = bankErrors.some(err =>
            err.accountName || err.branchName || err.branchNumber || err.accountNumber || err.accountKey || err.address
        );
        const hasWalletErrors = walletErrors.some(err =>
            err.walletType || err.phoneNumber || err.accountName || err.address
        );

        if (hasBankErrors || hasWalletErrors) {
            toast.error('Veuillez remplir correctement tous les champs requis.');
            return;
        }

        setIsLoading(true);
        try {
            const updateData = {
                ...formData,
                lastModifiedBy: currentUser.username, // Utiliser le username de l'utilisateur connecté
                recipientAccountInfo: {
                    ...formData.recipientAccountInfo,
                    bankAccountInfos: formData.recipientAccountInfo.bankAccountInfos,
                    walletAccountInfos: formData.recipientAccountInfo.walletAccountInfos
                }
            };

            // Afficher les données qui seront envoyées au backend
            /*//console.log('Données envoyées au backend:', JSON.stringify(updateData, null, 2));
            //console.log('Structure des données:', updateData);*/

            await RecipientService.updateRecipient(updateData);
            toast.success('Destinataire mis à jour avec succès!');
            onUpdate();
            handleClose();
            setIsEditing(false);
        } catch (error) {
            console.error('Error in updateRecipient:', error);
            toast.error('Erreur lors de la mise à jour du destinataire.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (recipient) {
            const { createdAt, modifiedAt, createdBy, ...updateableFields } = recipient;
            setFormData(updateableFields);
        }
    };

    const handleModalClose = () => {
        handleClose();
        setIsEditing(false);
    };

    const handlePhoneNumberChange = (index, value, country) => {
        if (/^\d{0,15}$/.test(value)) {
            const countryCode = countryToCodes[country];
            handleWalletAccountInfoChange(index, 'phoneNumber', `+${countryCode}${value}`);
        }
    };

    const addBankAccount = () => {
        const newBankAccount = {
            id: null,
            accountName: '',
            branchName: '',
            branchNumber: '',
            accountNumber: '',
            accountKey: '',
            city: formData.city || '',
            address: '',
            country: formData.country || ''
        };
        setFormData({
            ...formData,
            recipientAccountInfo: {
                ...formData.recipientAccountInfo,
                bankAccountInfos: [...formData.recipientAccountInfo.bankAccountInfos, newBankAccount]
            }
        });
        setTouchedFields({
            ...touchedFields,
            bankAccountInfos: [...touchedFields.bankAccountInfos, {}]
        });
        setErrorMessages({
            ...errorMessages,
            bankAccountInfos: [...errorMessages.bankAccountInfos, {}]
        });
    };

    const removeBankAccount = (index) => {
        const updatedBankAccounts = [...formData.recipientAccountInfo.bankAccountInfos];
        updatedBankAccounts.splice(index, 1);
        setFormData({
            ...formData,
            recipientAccountInfo: {
                ...formData.recipientAccountInfo,
                bankAccountInfos: updatedBankAccounts
            }
        });
        const updatedTouchedFields = [...touchedFields.bankAccountInfos];
        updatedTouchedFields.splice(index, 1);
        const updatedErrorMessages = [...errorMessages.bankAccountInfos];
        updatedErrorMessages.splice(index, 1);
        setTouchedFields({
            ...touchedFields,
            bankAccountInfos: updatedTouchedFields
        });
        setErrorMessages({
            ...errorMessages,
            bankAccountInfos: updatedErrorMessages
        });
    };

    const addWalletAccount = () => {
        const newWalletAccount = {
            id: null,
            phoneNumber: '',
            accountName: '',
            walletType: '',
            city: formData.city || '',
            address: '',
            country: formData.country || ''
        };
        setFormData({
            ...formData,
            recipientAccountInfo: {
                ...formData.recipientAccountInfo,
                walletAccountInfos: [...formData.recipientAccountInfo.walletAccountInfos, newWalletAccount]
            }
        });
        setTouchedFields({
            ...touchedFields,
            walletAccountInfos: [...touchedFields.walletAccountInfos, {}]
        });
        setErrorMessages({
            ...errorMessages,
            walletAccountInfos: [...errorMessages.walletAccountInfos, {}]
        });
    };

    const removeWalletAccount = (index) => {
        const updatedWalletAccounts = [...formData.recipientAccountInfo.walletAccountInfos];
        updatedWalletAccounts.splice(index, 1);
        setFormData({
            ...formData,
            recipientAccountInfo: {
                ...formData.recipientAccountInfo,
                walletAccountInfos: updatedWalletAccounts
            }
        });
        const updatedTouchedFields = [...touchedFields.walletAccountInfos];
        updatedTouchedFields.splice(index, 1);
        const updatedErrorMessages = [...errorMessages.walletAccountInfos];
        updatedErrorMessages.splice(index, 1);
        setTouchedFields({
            ...touchedFields,
            walletAccountInfos: updatedTouchedFields
        });
        setErrorMessages({
            ...errorMessages,
            walletAccountInfos: updatedErrorMessages
        });
    };

    return (
        <Modal size="lg" show={show} onHide={handleModalClose} scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{isEditing ? 'Modifier le destinataire' : 'Détails du destinataire'}</Modal.Title>
                {isEditing ? null : (
                    <Button variant="primary" style={{ marginLeft: '10px' }} onClick={handleEdit}>
                        <i className="fa-duotone fa-solid fa-pen-to-square"></i> Modifier
                    </Button>
                )}
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group controlId="firstName" className="form-floating mt-3">
                        <Form.Control
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            disabled={!isEditing}
                        />
                        <Form.Label>Prénom</Form.Label>
                    </Form.Group>
                    <Form.Group controlId="lastName" className="form-floating mt-3">
                        <Form.Control
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            disabled={!isEditing}
                        />
                        <Form.Label>Nom</Form.Label>
                    </Form.Group>
                    <Form.Group controlId="country" className="form-floating mt-3">
                        <Form.Select
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            disabled={!isEditing}
                        >
                            <option value="">Sélectionner un pays</option>
                            {countries.map((country, i) => (
                                <option key={i} value={country.name}>
                                    {country.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Label>Pays</Form.Label>
                    </Form.Group>
                    <Form.Group controlId="city" className="form-floating mt-3">
                        <Form.Control
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            disabled={!isEditing}
                        />
                        <Form.Label>Ville</Form.Label>
                    </Form.Group>
                    <Form.Group controlId="email" className="form-floating mt-3">
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={!isEditing}
                        />
                        <Form.Label>Email</Form.Label>
                    </Form.Group>

                    {/* Compte Bancaire (visible uniquement si le pays est le Cameroun) */}
                    {formData.country === 'Cameroun' && (
                        <>
                            <div className="mt-4 mb-4 d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 border-bottom"></div>
                                    <h5 className="mx-3 mb-0">Informations de Compte Bancaire</h5>
                                    <div className="flex-grow-1 border-bottom"></div>
                                </div>
                            </div>

                            {formData.recipientAccountInfo.bankAccountInfos.map((info, index) => (
                                <div key={index} className="mt-3 border p-3 rounded position-relative">
                                    {isEditing && (
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-2"
                                            onClick={() => removeBankAccount(index)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </Button>
                                    )}
                                    <Form.Group controlId={`accountName-${index}`} className="form-floating mt-5">
                                        <Form.Control
                                            type="text"
                                            value={info.accountName}
                                            onChange={(e) => handleBankAccountInfoChange(index, 'accountName', e.target.value)}
                                            disabled={!isEditing}
                                            isInvalid={touchedFields.bankAccountInfos[index]?.accountName && errorMessages.bankAccountInfos[index]?.accountName}
                                        />
                                        <Form.Label>Intitulé du compte</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            {errorMessages.bankAccountInfos[index]?.accountName}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId={`branchName-${index}`} className="form-floating mt-3">
                                        <Form.Select
                                            value={info.branchName}
                                            onChange={(e) => handleBankAccountInfoChange(index, 'branchName', e.target.value)}
                                            disabled={!isEditing}
                                            isInvalid={touchedFields.bankAccountInfos[index]?.branchName && errorMessages.bankAccountInfos[index]?.branchName}
                                        >
                                            <option value="">Sélectionner une banque</option>
                                            {banks.map((bank, i) => (
                                                <option key={i} value={bank.bankName}>
                                                    {bank.bankName}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Label>Nom de l'agence</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            {errorMessages.bankAccountInfos[index]?.branchName}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId={`branchNumber-${index}`} className="form-floating mt-3">
                                        <Form.Control
                                            type="text"
                                            value={info.branchNumber}
                                            onChange={(e) => handleBankAccountInfoChange(index, 'branchNumber', e.target.value)}
                                            disabled={!isEditing}
                                            isInvalid={touchedFields.bankAccountInfos[index]?.branchNumber && errorMessages.bankAccountInfos[index]?.branchNumber}
                                        />
                                        <Form.Label>Code agence</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            {errorMessages.bankAccountInfos[index]?.branchNumber}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId={`accountNumber-${index}`} className="form-floating mt-3">
                                        <Form.Control
                                            type="text"
                                            value={info.accountNumber}
                                            onChange={(e) => handleBankAccountInfoChange(index, 'accountNumber', e.target.value)}
                                            disabled={!isEditing}
                                            isInvalid={touchedFields.bankAccountInfos[index]?.accountNumber && errorMessages.bankAccountInfos[index]?.accountNumber}
                                        />
                                        <Form.Label>Numéro de Compte</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            {errorMessages.bankAccountInfos[index]?.accountNumber}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId={`accountKey-${index}`} className="form-floating mt-3">
                                        <Form.Control
                                            type="text"
                                            value={info.accountKey}
                                            onChange={(e) => handleBankAccountInfoChange(index, 'accountKey', e.target.value)}
                                            disabled={!isEditing}
                                            isInvalid={touchedFields.bankAccountInfos[index]?.accountKey && errorMessages.bankAccountInfos[index]?.accountKey}
                                        />
                                        <Form.Label>Clé de Compte</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            {errorMessages.bankAccountInfos[index]?.accountKey}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId={`address-${index}`} className="form-floating mt-3">
                                        <Form.Control
                                            type="text"
                                            value={info.address}
                                            onChange={(e) => handleBankAccountInfoChange(index, 'address', e.target.value)}
                                            disabled={!isEditing}
                                            isInvalid={touchedFields.bankAccountInfos[index]?.address && errorMessages.bankAccountInfos[index]?.address}
                                        />
                                        <Form.Label>Adresse (Quartier)</Form.Label>
                                        <Form.Control.Feedback type="invalid">
                                            {errorMessages.bankAccountInfos[index]?.address}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group controlId={`country-${index}`} className="form-floating mt-3">
                                        <Form.Control
                                            type="text"
                                            value={info.country}
                                            disabled
                                        />
                                        <Form.Label>Pays</Form.Label>
                                    </Form.Group>

                                    <Form.Group controlId={`city-${index}`} className="form-floating mt-3">
                                        <Form.Control
                                            type="text"
                                            value={info.city}
                                            disabled
                                        />
                                        <Form.Label>Ville</Form.Label>
                                    </Form.Group>
                                </div>
                            ))}

                            {isEditing && (
                                <div className="mt-3 d-flex justify-content-center align-items-center flex-column">
                                    <div className="d-flex flex-column align-items-center">
                                        <p style={{
                                            fontStyle: 'italic',
                                            marginBottom: '10px',
                                            color: '#007bff'
                                        }}>
                                            Cliquer sur le bouton ci-dessous pour ajouter un compte bancaire
                                        </p>

                                        <Button
                                            variant="outline-primary"
                                            onClick={addBankAccount}
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                borderRadius: '50%',
                                                border: '2px solid #007bff',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                fontSize: '30px',
                                                fontWeight: 'bold',
                                                padding: '0',
                                                backgroundColor: 'transparent'
                                            }}
                                            className="d-flex justify-content-center align-items-center"
                                        >
                                            +
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Compte Wallet */}
                    <div className="mt-4 mb-4 d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1 border-bottom"></div>
                            <h5 className="mx-3 mb-0">Informations de Compte Wallet</h5>
                            <div className="flex-grow-1 border-bottom"></div>
                        </div>
                    </div>

                    {formData.recipientAccountInfo.walletAccountInfos.map((info, index) => (
                        <div key={index} className="mt-3 border p-3 rounded position-relative">
                            {isEditing && (
                                <Button
                                    variant="danger"
                                    size="sm"
                                    className="position-absolute top-0 end-0 m-2"
                                    onClick={() => removeWalletAccount(index)}
                                >
                                    <i className="fas fa-trash"></i>
                                </Button>
                            )}
                            <Form.Group controlId={`walletType-${index}`} className="form-floating mt-5">
                                <Form.Select
                                    value={info.walletType}
                                    onChange={(e) => handleWalletAccountInfoChange(index, 'walletType', e.target.value)}
                                    disabled={!isEditing}
                                    isInvalid={touchedFields.walletAccountInfos[index]?.walletType && errorMessages.walletAccountInfos[index]?.walletType}
                                >
                                    <option value="">Sélectionner un type de wallet</option>
                                    {walletTypes.map((type, i) => (
                                        <option key={i} value={type.value}>{type.label}</option>
                                    ))}
                                </Form.Select>
                                <Form.Label>Type de Wallet</Form.Label>
                                <Form.Control.Feedback type="invalid">
                                    {errorMessages.walletAccountInfos[index]?.walletType}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={`phoneNumber-${index}`} className="form-floating mt-3">
                                <Form.Label>Numéro de Téléphone</Form.Label> <br/><br/>
                                <div className="d-flex align-items-center">
                                    <Flags code={countries.find(c => c.name === formData.country)?.code || "CM"} height="24" />
                                    <span className="ms-2">+{countryToCodes[formData.country] || "237"}</span>
                                    <span className="ms-2">
                                        <Form.Control
                                            type="text"
                                            value={info.phoneNumber ? info.phoneNumber.replace(`+${countryToCodes[formData.country]}`, '') : ''}
                                            onChange={(e) => handlePhoneNumberChange(index, e.target.value, formData.country)}
                                            disabled={!isEditing}
                                            maxLength={15}
                                            style={{ marginLeft: '10px' }}
                                            isInvalid={touchedFields.walletAccountInfos[index]?.phoneNumber && errorMessages.walletAccountInfos[index]?.phoneNumber}
                                        />
                                    </span>
                                    <span className="ms-2">
                                        <Form.Control.Feedback type="invalid">
                                            {errorMessages.walletAccountInfos[index]?.phoneNumber}
                                        </Form.Control.Feedback>
                                    </span>

                                </div>
                            </Form.Group>
                            <Form.Group controlId={`accountName-${index}`} className="form-floating mt-3">
                                <Form.Control
                                    type="text"
                                    value={info.accountName}
                                    onChange={(e) => handleWalletAccountInfoChange(index, 'accountName', e.target.value)}
                                    disabled={!isEditing}
                                    isInvalid={touchedFields.walletAccountInfos[index]?.accountName && errorMessages.walletAccountInfos[index]?.accountName}
                                />
                                <Form.Label>Nom du Compte Wallet</Form.Label>
                                <Form.Control.Feedback type="invalid">
                                    {errorMessages.walletAccountInfos[index]?.accountName}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId={`address-${index}`} className="form-floating mt-3">
                                <Form.Control
                                    type="text"
                                    value={info.address}
                                    onChange={(e) => handleWalletAccountInfoChange(index, 'address', e.target.value)}
                                    disabled={!isEditing}
                                    isInvalid={touchedFields.walletAccountInfos[index]?.address && errorMessages.walletAccountInfos[index]?.address}
                                />
                                <Form.Label>Adresse (Quartier)</Form.Label>
                                <Form.Control.Feedback type="invalid">
                                    {errorMessages.walletAccountInfos[index]?.address}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId={`country-${index}`} className="form-floating mt-3">
                                <Form.Control
                                    type="text"
                                    value={info.country}
                                    disabled
                                />
                                <Form.Label>Pays</Form.Label>
                            </Form.Group>

                            <Form.Group controlId={`city-${index}`} className="form-floating mt-3">
                                <Form.Control
                                    type="text"
                                    value={info.city}
                                    disabled
                                />
                                <Form.Label>Ville</Form.Label>
                            </Form.Group>
                        </div>
                    ))}

                    {isEditing && (
                        <div className="mt-3 d-flex justify-content-center align-items-center flex-column">
                            <div className="d-flex flex-column align-items-center">
                                <p style={{
                                    fontStyle: 'italic',
                                    marginBottom: '10px',
                                    color: '#007bff'
                                }}>
                                    Cliquer sur le bouton ci-dessous pour ajouter un wallet
                                </p>

                                <Button
                                    variant="outline-primary"
                                    onClick={addWalletAccount}
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        border: '2px solid #007bff',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '30px',
                                        fontWeight: 'bold',
                                        padding: '0',
                                        backgroundColor: 'transparent'
                                    }}
                                    className="d-flex justify-content-center align-items-center"
                                >
                                    +
                                </Button>
                            </div>
                        </div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {isEditing ? (
                    <>
                        <Button variant="secondary" onClick={handleCancel}>
                            Annuler
                        </Button>
                        <Button variant="primary" onClick={handleSave} disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    <span className="ms-2">Sauvegarde en cours...</span>
                                </>
                            ) : (
                                'Sauvegarder'
                            )}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="secondary" onClick={handleModalClose}>
                            Fermer
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default RecipientDetailModal;